import { createFileRoute, Link } from "@tanstack/react-router"
import { Eye, FileText, Lock, Shield } from "lucide-react"

import { generateMetaTags } from "@repo/seo/generate-meta-tags"
import { sharedConfig } from "~/lib/config/shared"

export const Route = createFileRoute("/_public-layout/privacy-policy")({
  component: PrivacyPolicyScreen,
  beforeLoad: async () => {
    return {
      title: "Privacy Policy",
    }
  },
  head: ({ match }) => ({
    meta: generateMetaTags({
      base: {
        title: `${match.context.title} | ${sharedConfig.appName}`,
        description: `Privacy Policy for ${sharedConfig.appName}`,
        url: sharedConfig.baseUrl + match.fullPath,
      },
    }),
  }),
})

function PrivacyPolicyScreen() {
  return (
    <div className="container mx-auto px-4 py-16 md:px-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-10">
          <Link
            to="/"
            className="mb-6 flex items-center gap-2 text-primary hover:underline"
          >
            ← Back to Home
          </Link>
          <h1 className="mb-4 font-bold text-4xl tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground text-xl">
            Your privacy matters to us. This policy outlines how we collect,
            use, and protect your personal information.
          </p>
        </div>

        <div className="mb-10 rounded-lg bg-muted p-8">
          <div className="mb-4 flex items-center gap-3">
            <Shield className="h-6 w-6 text-primary" />
            <h2 className="font-semibold text-2xl">Our Commitment</h2>
          </div>
          <p className="mb-4 text-muted-foreground">
            At {sharedConfig.appName}, we are committed to protecting your
            privacy and ensuring the security of your personal information. This
            Privacy Policy explains how we collect, use, disclose, and safeguard
            your information when you visit our website or use our services.
          </p>
          <p className="text-muted-foreground">
            Please read this Privacy Policy carefully. By accessing or using our
            website and services, you acknowledge that you have read,
            understood, and agree to be bound by all the terms of this Privacy
            Policy.
          </p>
        </div>

        <section className="mb-10">
          <div className="mb-4 flex items-center gap-3">
            <Eye className="h-6 w-6 text-primary" />
            <h2 className="font-semibold text-2xl">Information We Collect</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="mb-2 font-medium text-lg">Personal Information</h3>
              <p className="text-muted-foreground">
                We may collect personal information that you voluntarily provide
                to us when you:
              </p>
              <ul className="mt-2 list-inside list-disc space-y-1 text-muted-foreground">
                <li>Register for an account</li>
                <li>Sign up for our newsletter</li>
                <li>Fill out a contact form</li>
                <li>Participate in surveys or contests</li>
                <li>Communicate with us via email or social media</li>
              </ul>
              <p className="mt-2 text-muted-foreground">
                This information may include your name, email address, phone
                number, company name, and any other information you choose to
                provide.
              </p>
            </div>

            <div>
              <h3 className="mb-2 font-medium text-lg">
                Automatically Collected Information
              </h3>
              <p className="text-muted-foreground">
                When you access our website, we may automatically collect
                certain information about your device and usage patterns,
                including:
              </p>
              <ul className="mt-2 list-inside list-disc space-y-1 text-muted-foreground">
                <li>IP address and browser type</li>
                <li>Device type and operating system</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referring website addresses</li>
                <li>Geographic location (country or city level)</li>
              </ul>
            </div>

            <div>
              <h3 className="mb-2 font-medium text-lg">
                Cookies and Tracking Technologies
              </h3>
              <p className="text-muted-foreground">
                We use cookies, web beacons, and similar tracking technologies
                to enhance your experience on our website. These technologies
                help us remember your preferences, understand how you use our
                website, and improve our services.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-10">
          <div className="mb-4 flex items-center gap-3">
            <FileText className="h-6 w-6 text-primary" />
            <h2 className="font-semibold text-2xl">
              How We Use Your Information
            </h2>
          </div>

          <p className="mb-4 text-muted-foreground">
            We may use the information we collect for various purposes,
            including to:
          </p>

          <ul className="list-inside list-disc space-y-2 text-muted-foreground">
            <li>Provide, maintain, and improve our services</li>
            <li>Process transactions and send related information</li>
            <li>Respond to your comments, questions, and requests</li>
            <li>
              Send you technical notices, updates, security alerts, and support
              messages
            </li>
            <li>
              Communicate with you about products, services, offers, promotions,
              and events
            </li>
            <li>
              Monitor and analyze trends, usage, and activities in connection
              with our services
            </li>
            <li>
              Detect, investigate, and prevent fraudulent transactions and other
              illegal activities
            </li>
            <li>Personalize your experience on our website</li>
          </ul>
        </section>

        <section className="mb-10">
          <div className="mb-4 flex items-center gap-3">
            <Lock className="h-6 w-6 text-primary" />
            <h2 className="font-semibold text-2xl">Data Security</h2>
          </div>

          <p className="mb-4 text-muted-foreground">
            We have implemented appropriate technical and organizational
            security measures designed to protect the security of any personal
            information we process. However, please note that no electronic
            transmission or storage of information can be entirely secure, and
            we cannot guarantee that the information collected about you will
            not be accessed, viewed, disclosed, altered, or destroyed by breach
            of any of our physical, technical, or managerial safeguards.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 font-semibold text-2xl">Data Retention</h2>
          <p className="text-muted-foreground">
            We will only keep your personal information for as long as it is
            necessary for the purposes set out in this Privacy Policy, unless a
            longer retention period is required or permitted by law (such as
            tax, accounting, or other legal requirements).
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 font-semibold text-2xl">Your Rights</h2>
          <p className="mb-4 text-muted-foreground">
            Depending on your location, you may have certain rights regarding
            your personal information, including:
          </p>

          <ul className="list-inside list-disc space-y-2 text-muted-foreground">
            <li>
              The right to access the personal information we hold about you
            </li>
            <li>
              The right to request that we correct any personal information we
              hold about you
            </li>
            <li>
              The right to request that we delete any personal information we
              hold about you
            </li>
            <li>The right to withdraw consent</li>
            <li>The right to data portability</li>
            <li>The right to object to processing</li>
          </ul>

          <p className="mt-4 text-muted-foreground">
            To exercise any of these rights, please contact us using the
            information provided below.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 font-semibold text-2xl">
            Changes to This Privacy Policy
          </h2>
          <p className="text-muted-foreground">
            We may update our Privacy Policy from time to time. We will notify
            you of any changes by posting the new Privacy Policy on this page
            and updating the "Last Updated" date. You are advised to review this
            Privacy Policy periodically for any changes.
          </p>
          <p className="mt-4 text-muted-foreground text-sm">
            Last Updated: June 2023
          </p>
        </section>

        <section className="rounded-lg bg-muted p-8">
          <h2 className="mb-4 font-semibold text-2xl">Contact Us</h2>
          <p className="mb-4 text-muted-foreground">
            If you have any questions or concerns about this Privacy Policy,
            please contact us:
          </p>

          <div className="space-y-2 text-muted-foreground">
            <p>Email: privacy@{sharedConfig.appName.toLowerCase()}.com</p>
            <p>
              Address: 123 Innovation Drive, Suite 200, San Francisco, CA 94103
            </p>
            <p>Phone: +1 (555) 123-4567</p>
          </div>
        </section>
      </div>
    </div>
  )
}
